import { PreviewCart } from "../cart/preview-cart";
import { ProductVariants } from "../product/product-variants";
import { GeneralOverlay } from "../general/general-overlay";
import { debounce } from "@app/scripts/utils/debounce";
import Swiper from 'swiper';

class ProductCard extends HTMLElement {
    private hoverCardContainer: HTMLElement | null = null;
    private isHoverActive: boolean = false;
    private swiper: Swiper | null = null;
    
    constructor() {
        super();
        this.setupHoverEffects();
        this.bindEvents();
        this.initMobileSwiper();
    }

    // Initialize Swiper for mobile only
    private initMobileSwiper() {
        if (window.innerWidth <= 750) {
            const swiperContainer = this.querySelector('.product-card__swiper');
            if (swiperContainer) {
                const isInSplide = this.closest('.splide__slide') !== null;
                
                this.swiper = new Swiper(swiperContainer as HTMLElement, {
                    slidesPerView: 1,
                    spaceBetween: 0,
                    loop: true,
                    // Disable touch/swipe if inside Splide to prevent conflicts
                    allowTouchMove: !isInSplide,
                    touchRatio: isInSplide ? 0 : 1,
                    touchAngle: 45,
                    grabCursor: !isInSplide,
                });
            }
        }

        // Handle resize events to initialize/destroy swiper
        window.addEventListener('resize', debounce(() => {
            if (window.innerWidth <= 750) {
                if (!this.swiper) {
                    this.initMobileSwiper();
                }
            } else {
                if (this.swiper) {
                    this.swiper.destroy();
                    this.swiper = null;
                }
            }
        }, 300));
    }

    bindEvents() {
        const quickAddElements = this.querySelectorAll(".product-card__quick-add");
        quickAddElements.forEach((element: any) => {
            element?.addEventListener("click", this.openQuickAddPanel.bind(this));
        })

        const addToCartButton = this.querySelector(".product__cta-button");
        addToCartButton?.addEventListener("click", this.onAddToCartClick.bind(this));

        if (window.matchMedia('(hover: hover)').matches) {
            this.addEventListener("mouseleave", this.closeQuickAddPanel.bind(this));
        }

        const productVariantInputs = this.querySelectorAll("product-variants input");
        const productVariants = new ProductVariants();
        productVariantInputs.forEach((input) => {
            const variantId = input.getAttribute("data-id") as string;

            input.addEventListener("change", () => {
                productVariants.onVariantSelect(variantId, this)
            })
        })

        const closeQuickAddButton = this.querySelector(".product-card__close-quick-add-button");
        closeQuickAddButton?.addEventListener("click", this.closeQuickAddPanel.bind(this));

        const overlayEle = this.querySelector('.product-card__overlay');
        overlayEle?.addEventListener("click", this.closeQuickAddPanel.bind(this));
    }

    private setupHoverEffects() {
        this.checkAndSetupHoverListeners();
        
        window.addEventListener('resize', debounce(() => {
            if (window.innerWidth <= 750) {
                this.removeHoverCard();
                this.removeHoverListeners();
            } else {
                this.checkAndSetupHoverListeners();
            }
        }, 250));
    }

    private checkAndSetupHoverListeners() {
        if (window.innerWidth <= 750) return;

        // Remove existing listeners to avoid duplicates
        this.removeHoverListeners();

        this.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
        this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    }

    private removeHoverListeners() {
        this.removeEventListener('mouseenter', this.handleMouseEnter.bind(this));
        this.removeEventListener('mouseleave', this.handleMouseLeave.bind(this));
    }

    private handleMouseEnter() {
        if (window.innerWidth <= 750) return;

        if (this.querySelector('.product-card__quick-add-panel.open')) return;

        this.isHoverActive = true;
        this.createHoverCard();
    }
    
    private handleMouseLeave() {
        this.isHoverActive = false;
        this.removeHoverCard();
    }
    
    private createHoverCard() {
        this.removeHoverCard();

        if (!this.isHoverActive) return;

        this.classList.add('has-hover-content');

        this.hoverCardContainer = document.createElement('div');
        this.hoverCardContainer.className = 'product-card__hover-content';

        const quickAddButtons = this.querySelector('.product-card__quick-buttons.desktop');
        if (quickAddButtons) {
            this.hoverCardContainer.appendChild(quickAddButtons);
        }

        const infoSection = this.querySelector('.product-card__wrapper > .product-card__info');
        if (infoSection) {
            const infoClone = infoSection.cloneNode(true) as HTMLElement;
            this.hoverCardContainer.appendChild(infoClone);
        }

        const ratingSection = this.querySelector('.product-card__rating');
        if (ratingSection) {
            const ratingClone = ratingSection.cloneNode(true) as HTMLElement;
            this.hoverCardContainer.appendChild(ratingClone);
        }


        this.appendChild(this.hoverCardContainer);

        this.positionHoverCard();
    }
    
    private positionHoverCard() {
        if (!this.hoverCardContainer) return;

        const imgSection = this.querySelector('.product-card__img');
        const imgHeight = imgSection ? imgSection.getBoundingClientRect().height : 0;
        const cardHeight = this.getBoundingClientRect().height;
        const quickAddButtonsHeight = this.hoverCardContainer.querySelector('.product-card__quick-buttons.desktop')?.getBoundingClientRect().height || 0;
        const minHoverHeight = cardHeight - imgHeight + quickAddButtonsHeight;

        this.hoverCardContainer.style.top = `${imgHeight}px`;
        this.hoverCardContainer.style.minHeight = `${minHoverHeight}px`;
    }
    
    private removeHoverCard() {
        if (this.hoverCardContainer) {
            const quickAddButtons = this.hoverCardContainer.querySelector('.product-card__quick-buttons.desktop');
            if (quickAddButtons) {
                const imgSection = this.querySelector('.product-card__img');
                if (imgSection) {
                    imgSection.appendChild(quickAddButtons);
                }
            }

            this.hoverCardContainer.remove();
            this.hoverCardContainer = null;
            
            this.classList.remove('has-hover-content');
        }
    }

    openQuickAddPanel(event: Event) {
        event.stopPropagation();
        event.preventDefault();
        const quickAddPanelEle = this.querySelector('.product-card__quick-add-panel');
        quickAddPanelEle?.classList.add('open');

        const overlayEle = this.querySelector('.product-card__overlay');

        const target = event.currentTarget as HTMLElement;
        const isMobile = target.classList.contains("mobile");
        const template = document.querySelector("body")?.getAttribute("data-template");

        if(isMobile && template == "collection") {
            const generalOverlay = new GeneralOverlay();
            generalOverlay.applyOverlay(quickAddPanelEle);
        }

        if(window.innerWidth <=750 && template =="collection"){
            // show overlay and disable body scroll
            overlayEle?.classList.add('open');
            document.body.style.overflow = 'hidden';
        }
    }

    closeQuickAddPanel() {
        const template = document.querySelector("body")?.getAttribute("data-template");
        const quickAddPanelEle =  this.querySelector('.product-card__quick-add-panel');
        quickAddPanelEle?.classList.remove('open');
        
        const overlayEle = this.querySelector('.product-card__overlay');
        if(window.innerWidth <=750 && template == "collection"){
            // hide overlay and enable body scroll
            overlayEle?.classList.remove('open');
            document.body.style.overflow = '';
        }
    
        // const screenWidth = window.innerWidth;

        // if (screenWidth <= 750) {
        //     const generalOverlay = new GeneralOverlay();
        //     generalOverlay.removeOverlay(quickAddPanelEle);
        // }       
    }

    onAddToCartClick(event: Event) {
        event.stopPropagation();
        event.preventDefault();
        this.closeQuickAddPanel();
        const variantInput = this.querySelector('product-variants input[type="radio"]:checked') as HTMLInputElement;
        const itemKey = variantInput.value;
        const quantityInput = this.querySelector('product-quantity input[type="number"]') as HTMLInputElement;
        const quantity = Number(quantityInput.value);

        const previewCart = new PreviewCart();

        previewCart.addItem(itemKey, quantity)
        .then((data: any) => {
            if (data.status && data.status >= 400) {
                previewCart.closePreviewCart();
                this.querySelectorAll(".product-form__error-message-wrapper").forEach((ele: any) => {
                    previewCart.cartErrorHandling(ele, data.description);
                })

                const quickAddElement = this.querySelector(".product-card__quick-add.mobile") as HTMLElement;
                quickAddElement.click();
            }
            else {
                previewCart.refreshPreviewCart(data);
            }
        })
        .catch((error) => {
            console.error("error", error)
        })
    }
}

customElements.define("product-card", ProductCard);

